import type { api } from '@agency/shared';
import { Button, Checkbox, Chip, Field, Input } from '@agency/ui';
import { RefreshCw } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { ErrorCallout } from '../../app/widgets.js';
import { useMutation } from '../../lib/mutate.js';

export function ModelMultiSelect({ credential, value, onChange }: {
  credential: api.RouteRequest<'admin.aiKeys.models'>;
  value: string[];
  onChange: (models: string[]) => void;
}) {
  const mutation = useMutation();
  const [catalog, setCatalog] = useState<api.RouteResponse<'admin.aiKeys.models'> | null>(null);
  const [search, setSearch] = useState('');
  const generation = useRef(0);
  const { secret, baseUrl, projectId, organizationId } = credential;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  useEffect(() => {
    generation.current++;
    setCatalog(null);
    onChangeRef.current([]);
    return () => { generation.current++; };
  }, [secret, baseUrl, projectId, organizationId]);

  const load = async () => {
    const current = ++generation.current;
    const result = await mutation.run('admin.aiKeys.models', { input: credential }, { silent: true });
    if (current !== generation.current) return;
    setCatalog(result);
    onChange(result ? value.filter((id) => result.models.some((model) => model.id === id)) : []);
  };
  const matches = catalog?.models.filter((model) => model.id.toLowerCase().includes(search.toLowerCase())) ?? [];
  return (
    <Field label="Permitted models" required help="Loaded from OpenAI for this project key, newest first. Select the models Rumi may use; its default model is configured under Model config.">
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs text-mute">{catalog ? `${catalog.models.length} available · ${value.length} selected` : 'Enter the project API key, then load its available models.'}</span>
          <Button size="sm" variant="tertiary" icon={RefreshCw} disabled={secret.trim().length < 8} loading={mutation.busy} onClick={() => void load()}>{catalog ? 'Refresh models' : 'Load models'}</Button>
        </div>
        <ErrorCallout error={mutation.error} />
        {catalog ? <>
          <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search available models" aria-label="Search available models" />
          {value.length ? <div className="flex flex-wrap gap-1.5">{value.map((id) => <Chip key={id} tone="mute">{id}</Chip>)}</div> : null}
          <div role="group" aria-label="Available models" className="max-h-56 overflow-y-auto rounded-sm border border-hair p-3">
            {matches.length === 0 ? <p className="text-sm text-mute">{catalog.models.length ? 'No models match your search.' : 'No models are available to this key.'}</p> : <div className="flex flex-col gap-3">{matches.map((model) => (
              <Checkbox key={model.id} checked={value.includes(model.id)} label={<span className="font-mono text-xs-plus">{model.id}</span>} description={model.shutdownDate ? `Available until ${model.shutdownDate}` : undefined} onCheckedChange={(checked) => onChange(checked ? [...value, model.id] : value.filter((id) => id !== model.id))} />
            ))}</div>}
          </div>
          <p className="text-xs text-mute">Availability does not guarantee support for Rumi’s text and tool requests. Choose text models supported by the Responses API.</p>
        </> : null}
      </div>
    </Field>
  );
}
